/**
 * Resolve the `redirectPath` query param an already-authenticated visitor
 * arrives at `/login` with into a safe in-app path to navigate to.
 *
 * `redirectPath` is attacker-controllable (it comes straight off the URL), so
 * this guards against:
 *  - off-origin destinations / open redirects — `https://evil.com`, `//evil.com`,
 *    `/\evil.com`, `javascript:…` all resolve to an origin that isn't ours;
 *  - a same-origin URL carrying a protocol-relative *pathname* — e.g.
 *    `/.//evil.com` parses to pathname `//evil.com`, which would navigate
 *    off-site if handed to the router as `//evil.com`;
 *  - bouncing back to `/login` (or `/logout`), which would trap the visitor on
 *    a blank page since `LoginContainer` renders `null` and the redirect only
 *    fires once.
 *
 * Anything that doesn't pass falls back to the dashboard (`/`).
 */
export function resolveAuthedRedirect(redirectPath: string | null | undefined): string {
  if (!redirectPath) {
    return '/';
  }

  let url: URL;
  try {
    // Resolve against the current origin so a relative path (e.g. `/studies/x`)
    // parses instead of throwing the way a bare `new URL(path)` would.
    url = new URL(redirectPath, window.location.origin);
  } catch {
    return '/';
  }

  // Off-origin hosts and non-http(s) schemes (their origin is a different host
  // or `null`) never redirect anywhere but the dashboard.
  if (url.origin !== window.location.origin) {
    return '/';
  }

  const path = `${url.pathname}${url.search}`;

  // Reject a protocol-relative-looking path (`//host`) — only a single-slash
  // absolute path is a genuine in-app route.
  if (!/^\/(?![/\\])/.test(path)) {
    return '/';
  }

  // Never send an authenticated visitor back to the auth routes.
  if (url.pathname === '/login' || url.pathname === '/logout') {
    return '/';
  }

  return path;
}
